import { ReactElement, useState } from 'react';
import { HiChevronRight } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import { ChainModel } from 'types/ChainModel';
import {
  useChainModels,
  useUpdateChainModel,
} from 'hooks/useChainModels';
import { useUser } from '../../hooks/useUser';

export default function ModelsView(): ReactElement {
  const [userProfile] = useUser();
  const teamId: string | null =
    userProfile?.team_id ?? null;

  const {
    data: chainModels,
    isLoading,
  } = useChainModels();
  const updateMutation = useUpdateChainModel();

  const [expandedId, setExpandedId] = useState<
    string | null
  >(null);

  const teamModels = (chainModels ?? []).filter(
    (m: ChainModel) =>
      m.team_id === teamId || m.team_id === null
  );

  const currentDefault = teamModels.find(
    (m) => m.is_default && m.team_id === teamId
  );

  const handleSetDefault = (model: ChainModel) => {
    if (model.team_id === null) {
      toast.error('Cannot set a global model as team default');
      return;
    }
    if (currentDefault && currentDefault.id !== model.id) {
      updateMutation.mutate(
        {
          id: currentDefault.id,
          data: { is_default: false },
        },
        {
          onSuccess: () => {
            updateMutation.mutate(
              {
                id: model.id,
                data: { is_default: true },
              },
              {
                onSuccess: () =>
                  toast.success(
                    `${model.name} set as team default`
                  ),
                onError: () =>
                  toast.error('Failed to update default'),
              }
            );
          },
          onError: () =>
            toast.error('Failed to clear previous default'),
        }
      );
    } else {
      updateMutation.mutate(
        {
          id: model.id,
          data: { is_default: true },
        },
        {
          onSuccess: () =>
            toast.success(
              `${model.name} set as team default`
            ),
          onError: () =>
            toast.error('Failed to update default'),
        }
      );
    }
  };

  return (
    <div className="cr-mx-auto cr-max-w-3xl cr-px-4">
      <SectionHeader title="Models" isLoading={isLoading} />

      {/* Chain Models */}
      <div className="cr-mb-6">
        <h2
          className="cr-mb-3 cr-text-sm cr-font-semibold
            cr-uppercase cr-tracking-wide cr-text-gray-500"
        >
          Chain Models
        </h2>

        {isLoading ? (
          <div className="cr-flex cr-items-center cr-gap-2
            cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-sm"
          >
            <Spinner isFetching />
            <span className="cr-text-sm cr-text-gray-500">
              Loading models...
            </span>
          </div>
        ) : teamModels.length === 0 ? (
          <div className="cr-rounded-lg cr-bg-white cr-p-6
            cr-text-center cr-shadow-sm"
          >
            <p className="cr-text-sm cr-text-gray-400">
              No chain models available
            </p>
          </div>
        ) : (
          <div className="cr-space-y-2">
            {teamModels.map((model: ChainModel) => {
              const isDefault =
                model.is_default &&
                model.team_id === teamId;
              const isGlobalDefault =
                model.is_default &&
                model.team_id === null;
              const isExpanded = expandedId === model.id;
              const isTeamModel = model.team_id !== null;

              return (
                <div
                  key={model.id}
                  className={[
                    'cr-rounded-lg cr-border cr-bg-white',
                    'cr-shadow-sm cr-transition-all',
                    'cr-duration-200',
                    isDefault
                      ? 'cr-border-blue-400'
                      : 'cr-border-gray-200',
                  ].join(' ')}
                >
                  {/* Header row */}
                  <div
                    className="cr-flex cr-cursor-pointer
                      cr-items-center cr-justify-between
                      cr-px-4 cr-py-3"
                    onClick={() =>
                      setExpandedId(
                        isExpanded ? null : model.id
                      )
                    }
                  >
                    <div className="cr-flex cr-items-center
                      cr-gap-2 cr-min-w-0"
                    >
                      <span className="cr-font-medium
                        cr-text-gray-800 cr-truncate"
                      >
                        {model.name}
                      </span>
                      {isDefault && (
                        <span className="cr-shrink-0
                          cr-rounded-full cr-bg-blue-100
                          cr-px-2 cr-py-0.5 cr-text-xs
                          cr-font-medium cr-text-blue-700"
                        >
                          Team Default
                        </span>
                      )}
                      {isGlobalDefault && (
                        <span className="cr-shrink-0
                          cr-rounded-full cr-bg-green-50
                          cr-px-2 cr-py-0.5 cr-text-xs
                          cr-font-medium cr-text-green-900"
                        >
                          Global Default
                        </span>
                      )}
                      {!isTeamModel && !isGlobalDefault && (
                        <span className="cr-shrink-0
                          cr-rounded-full cr-bg-gray-100
                          cr-px-2 cr-py-0.5 cr-text-xs
                          cr-font-medium cr-text-gray-500"
                        >
                          Global
                        </span>
                      )}
                    </div>
                    <HiChevronRight
                      className={[
                        'cr-h-5 cr-w-5 cr-shrink-0',
                        'cr-text-gray-400',
                        'cr-transition-transform',
                        'cr-duration-200',
                        isExpanded ? 'cr-rotate-90' : '',
                      ].join(' ')}
                    />
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="cr-border-t
                      cr-border-gray-100 cr-px-4 cr-py-3"
                    >
                      {model.notes && (
                        <p className="cr-mb-2 cr-text-sm
                          cr-text-gray-600"
                        >
                          {model.notes}
                        </p>
                      )}

                      {model.resolved_model && (
                        <details className="cr-mb-3">
                          <summary className="cr-cursor-pointer
                            cr-text-xs cr-font-medium
                            cr-text-gray-500"
                          >
                            Model Parameters
                          </summary>
                          <pre className="cr-mt-1
                            cr-max-h-48 cr-overflow-auto
                            cr-rounded cr-bg-gray-100
                            cr-p-2 cr-text-xs
                            cr-text-gray-700"
                          >
                            {JSON.stringify(
                              model.resolved_model,
                              null,
                              2
                            )}
                          </pre>
                        </details>
                      )}

                      {/* Set as default button */}
                      {isTeamModel && !isDefault && (
                        <button
                          className="cr-rounded-md
                            cr-bg-blue-50 cr-px-3 cr-py-1.5
                            cr-text-sm cr-font-medium
                            cr-text-blue-700
                            cr-transition-colors
                            cr-duration-200
                            hover:cr-bg-blue-100
                            disabled:cr-opacity-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefault(model);
                          }}
                          disabled={
                            updateMutation.isPending
                          }
                        >
                          {updateMutation.isPending
                            ? 'Updating...'
                            : 'Set as Team Default'}
                        </button>
                      )}
                      {isDefault && (
                        <span className="cr-text-xs
                          cr-text-blue-500"
                        >
                          This is the current team default
                        </span>
                      )}
                      {!isTeamModel && (
                        <span className="cr-text-xs
                          cr-text-gray-400"
                        >
                          Global models are managed by admins
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Default info */}
        {!isLoading && teamModels.length > 0 && (
          <p className="cr-mt-3 cr-text-xs cr-text-gray-400">
            {currentDefault
              ? `Team default: ${currentDefault.name}`
              : 'No team default set — global default will be used'}
          </p>
        )}
      </div>
    </div>
  );
}
